/**
 * Manufacturer / family tier-name vocabulary — pure helpers for the admin.
 *
 * Design memo: Confluence "Manufacturer Tier Vocabulary — design memo"
 * (WK 2286452737). The vocabulary is `TierVocabularyEntry[]` stored flat at
 * `movement_manufacturers/{id}.tierVocabulary` and
 * `calibre_families/{id}.tierVocabulary`. An entry with a `brandId` is a BRAND
 * tier (one brand's own modification); without one it is a GENERIC grade.
 *
 * MIRROR: `foldTierName` and `looksLikeTierSuffixCode` are duplicated by hand
 * in functions/src/tierVocabulary.ts. Change both or neither.
 */

import type { TierVocabularyEntry } from '@watchkeeper/shared-types';

/** Accent-, case- and whitespace-insensitive key: "Spécial" ≡ "special". */
export function foldTierName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generation / execution suffixes and short codes (`-1`, `/270`, `b`, `SQ`,
 * `CSL`, `L888.4`). Never grades (memo D10); they stay at family level and never
 * get the promote-to-manufacturer nudge (D23).
 */
export function looksLikeTierSuffixCode(name: string): boolean {
  const t = name.trim();
  if (!t) return true;
  if (/^[-/.]/.test(t)) return true;
  if (/^[A-Z]{1,3}$/.test(t)) return true;
  if (/^[a-z]$/.test(t)) return true;
  if (/^[A-Za-z]{0,3}\d+(\.\d+)?[A-Za-z]{0,2}$/.test(t)) return true;
  return false;
}

export function isGenericTierEntry(e: Pick<TierVocabularyEntry, 'brandId'>): boolean {
  return !e.brandId;
}

/**
 * The silent clean-ups (memo "Validation → Silent"): trim, drop a synonym equal
 * to its own canonical, drop duplicate synonyms, drop empty optionals. Never
 * writes `undefined` — Firestore rejects it inside an array.
 */
export function normaliseTierEntry(e: TierVocabularyEntry): TierVocabularyEntry {
  const canonicalName = e.canonicalName.trim();
  const own = foldTierName(canonicalName);
  const seen = new Set<string>([own]);
  const akas: string[] = [];
  for (const raw of e.akas ?? []) {
    const a = raw.trim();
    // Exact-spelling de-dupe: "Special" and "Spécial" are both worth keeping
    // as recognised spellings, but the same spelling twice is noise.
    const exact = a.toLowerCase();
    if (!a || foldTierName(a) === own || seen.has(exact)) continue;
    seen.add(exact);
    akas.push(a);
  }
  const out: TierVocabularyEntry = { id: e.id, canonicalName, akas };
  if (typeof e.rank === 'number' && Number.isFinite(e.rank)) out.rank = e.rank;
  const description = e.description?.trim();
  if (description) out.description = description;
  const brandId = e.brandId?.trim();
  if (brandId) out.brandId = brandId;
  return out;
}

export interface TierVocabularyIssues {
  /** Hard errors (D7) — a write with any of these is refused. */
  errors: string[];
  /** Shown, never blocking. */
  warnings: string[];
}

/**
 * Validate one owner's list (memo "Validation").
 *
 * Hard errors: a missing canonical name; a generic canonical or synonym that
 * matches another generic entry's canonical or synonym; a brand tier whose name
 * or synonym collides with another tier of the SAME brand, or with any generic
 * name (a brand tier must never shadow a grade). Two different brands may share
 * a name. Matching folds accents and case.
 *
 * Warnings: synonyms under 3 characters, and names that look like generation
 * suffixes.
 *
 * `reserved` are names of ANOTHER list that this one must not shadow — the
 * family editor passes the manufacturer's generic names, so a family BRAND
 * tier can't take a grade's name. (Family generic names matching a
 * manufacturer grade are drift, not an error — D15.)
 */
export function validateTierVocabulary(
  entries: TierVocabularyEntry[],
  reserved: TierVocabularyEntry[] = [],
): TierVocabularyIssues {
  const errors: string[] = [];
  const warnings: string[] = [];
  // Manufacturer and family lists are GENERIC grade names only (James,
  // 2026-10-29). A brand-specific tier lives on one calibre's own tier list.
  for (const e of entries) {
    if (e.brandId?.trim()) {
      errors.push(
        `"${e.canonicalName}" is a brand tier. Brand tiers live on the calibre now, not on manufacturer or family tier names — add it on the calibre's Tiers.`,
      );
    }
  }
  // scope ('' = generic, else brandId) → fold → canonical of the claiming entry
  const claims = new Map<string, Map<string, string>>();
  const claim = (scope: string, name: string, ownerId: string) => {
    const k = foldTierName(name);
    if (!k) return;
    let m = claims.get(scope);
    if (!m) claims.set(scope, (m = new Map()));
    const prior = m.get(k);
    if (prior !== undefined && prior !== ownerId) {
      errors.push(
        `"${name}" is already used by another ${scope ? 'tier of this brand' : 'grade'} — ` +
          `each spelling can belong to one tier only.`,
      );
      return;
    }
    m.set(k, ownerId);
  };

  const genericKeys = new Set<string>();
  for (const r of reserved.filter(isGenericTierEntry)) {
    for (const n of [r.canonicalName, ...r.akas]) genericKeys.add(foldTierName(n));
  }

  entries.forEach((raw, i) => {
    const e = normaliseTierEntry(raw);
    if (!e.canonicalName) {
      errors.push(`Row ${i + 1} has no canonical name.`);
      return;
    }
    const scope = e.brandId ?? '';
    const ownerId = e.id || `#${i}`;
    for (const n of [e.canonicalName, ...e.akas]) {
      claim(scope, n, ownerId);
      if (!scope) genericKeys.add(foldTierName(n));
    }
    for (const a of e.akas) {
      if (a.length < 3) warnings.push(`"${a}" (on ${e.canonicalName}) is very short.`);
      if (looksLikeTierSuffixCode(a)) warnings.push(`"${a}" (on ${e.canonicalName}) looks like a generation suffix, not a grade spelling.`);
    }
    if (!scope && looksLikeTierSuffixCode(e.canonicalName)) {
      warnings.push(`"${e.canonicalName}" looks like a generation suffix, not a grade.`);
    }
  });

  // Brand tiers must not shadow any generic name (this list's or the reserved one).
  for (const raw of entries) {
    const e = normaliseTierEntry(raw);
    if (!e.brandId) continue;
    for (const n of [e.canonicalName, ...e.akas]) {
      if (genericKeys.has(foldTierName(n))) {
        errors.push(`Brand tier "${e.canonicalName}": "${n}" is a generic grade name — a brand tier can't shadow a grade.`);
      }
    }
  }
  return { errors: [...new Set(errors)], warnings: [...new Set(warnings)] };
}

/** Generic grades by rank (unranked after, stored order), then brand tiers grouped by brand. */
export function sortTierVocabulary(entries: TierVocabularyEntry[]): TierVocabularyEntry[] {
  const indexed = entries.map((e, i) => ({ e, i }));
  return indexed
    .sort((a, b) => {
      const ga = a.e.brandId ? 1 : 0;
      const gb = b.e.brandId ? 1 : 0;
      if (ga !== gb) return ga - gb;
      if (ga === 1 && a.e.brandId !== b.e.brandId) return (a.e.brandId ?? '').localeCompare(b.e.brandId ?? '');
      const ra = a.e.rank ?? Number.POSITIVE_INFINITY;
      const rb = b.e.rank ?? Number.POSITIVE_INFINITY;
      return ra === rb ? a.i - b.i : ra - rb;
    })
    .map((x) => x.e);
}

export function newTierEntryId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tier-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Insert or replace by id. */
export function upsertTierEntry(list: TierVocabularyEntry[], entry: TierVocabularyEntry): TierVocabularyEntry[] {
  const clean = normaliseTierEntry(entry);
  const i = list.findIndex((e) => e.id === clean.id);
  if (i < 0) return [...list, clean];
  const next = [...list];
  next[i] = clean;
  return next;
}

export function removeTierEntry(list: TierVocabularyEntry[], id: string): TierVocabularyEntry[] {
  return list.filter((e) => e.id !== id);
}

/** Append synonyms to one entry (skipping any it already has). */
export function addAkasToTierEntry(list: TierVocabularyEntry[], id: string, akas: string[]): TierVocabularyEntry[] {
  return list.map((e) => (e.id === id ? normaliseTierEntry({ ...e, akas: [...e.akas, ...akas] }) : e));
}

/** One ticked row of the ✨ Suggest tier names review (see the callable's response). */
export interface AcceptedTierSuggestion {
  canonicalName: string;
  akas: string[];
  rank?: number;
  description?: string;
  matchesExistingEntryId?: string;
}

/**
 * Apply the ticked suggestions, add-only (D22): a row matching a curator entry
 * only adds synonyms to it; anything else becomes a new generic entry. A
 * synonym another entry already claims is skipped rather than failing the whole
 * merge — the list is re-read inside the transaction, so it can have moved on
 * since the dialog ran.
 */
export function mergeSuggestedTiers(
  current: TierVocabularyEntry[],
  accepted: AcceptedTierSuggestion[],
): TierVocabularyEntry[] {
  let next = [...current];
  const claimed = () => {
    const m = new Map<string, string>();
    for (const e of next.filter(isGenericTierEntry)) {
      for (const n of [e.canonicalName, ...e.akas]) m.set(foldTierName(n), e.id);
    }
    return m;
  };
  for (const s of accepted) {
    const owners = claimed();
    const target =
      (s.matchesExistingEntryId && next.find((e) => e.id === s.matchesExistingEntryId && isGenericTierEntry(e))) ||
      next.find((e) => e.id === owners.get(foldTierName(s.canonicalName)));
    if (target) {
      const add = s.akas.filter((a) => {
        const o = owners.get(foldTierName(a));
        return o === undefined || o === target.id;
      });
      next = addAkasToTierEntry(next, target.id, add);
      continue;
    }
    const entry: TierVocabularyEntry = {
      id: newTierEntryId(),
      canonicalName: s.canonicalName,
      akas: s.akas.filter((a) => !owners.has(foldTierName(a))),
      ...(s.rank !== undefined ? { rank: s.rank } : {}),
      ...(s.description ? { description: s.description } : {}),
    };
    next = upsertTierEntry(next, entry);
  }
  return next;
}

// ---------------------------------------------------------------------------
// Promote prompt (family → manufacturer), memo "Promote prompt"
// ---------------------------------------------------------------------------

export interface FamilyTierNamesSource {
  id: string;
  name: string;
  tierNames?: string[];
  tierVocabulary?: TierVocabularyEntry[];
}

export interface PromoteEvidence {
  /** Signal A: the manufacturer grade this name looks like, if any. */
  nearMatch?: TierVocabularyEntry;
  /** True when the name is EXACTLY one of that grade's spellings. */
  exactSynonym: boolean;
  /** Signal B: other families (same brand scope) already using the name. */
  otherFamilies: Array<{ id: string; name: string }>;
}

/** Strip a bracketed part: "Special (Elaboré)" → ["Special", "Elaboré"]. */
function nameParts(name: string): string[] {
  const parts = [name];
  const m = name.match(/^(.*?)\s*[([](.+?)[)\]]\s*$/);
  if (m) parts.push(m[1], m[2]);
  return parts.map(foldTierName).filter(Boolean);
}

/**
 * Evidence that a family-level name is really manufacturer-wide (D16). Suffix
 * codes never qualify (D23). A brand tier only looks at the same brand's names
 * in other families, and never near-matches a generic grade.
 */
export function promoteEvidence(
  entry: Pick<TierVocabularyEntry, 'canonicalName' | 'brandId'>,
  manufacturerEntries: TierVocabularyEntry[],
  otherFamilies: FamilyTierNamesSource[],
): PromoteEvidence | null {
  const name = entry.canonicalName.trim();
  if (!name || looksLikeTierSuffixCode(name)) return null;
  const key = foldTierName(name);
  const parts = nameParts(name);

  let nearMatch: TierVocabularyEntry | undefined;
  let exactSynonym = false;
  if (!entry.brandId) {
    for (const m of manufacturerEntries.filter(isGenericTierEntry)) {
      const spellings = [m.canonicalName, ...m.akas].map(foldTierName);
      if (spellings.includes(key)) {
        nearMatch = m;
        exactSynonym = true;
        break;
      }
      const words = new Set(spellings.flatMap((s) => s.split(/[\s()[\]/+-]+/)).filter((w) => w.length >= 3));
      const nameWords = key.split(/[\s()[\]/+-]+/).filter((w) => w.length >= 3);
      if (parts.some((p) => spellings.includes(p)) || nameWords.some((w) => words.has(w))) {
        nearMatch = m;
        break;
      }
    }
  }

  const others = otherFamilies
    .filter((f) => {
      const structured = (f.tierVocabulary ?? []).some(
        (e) => (e.brandId ?? '') === (entry.brandId ?? '') && [e.canonicalName, ...e.akas].some((n) => foldTierName(n) === key),
      );
      // Legacy names carry no brand, so they only count for a generic name.
      const legacy = !entry.brandId && (f.tierNames ?? []).some((n) => foldTierName(n) === key);
      return structured || legacy;
    })
    .map((f) => ({ id: f.id, name: f.name }));

  if (!nearMatch && others.length === 0) return null;
  return { nearMatch, exactSynonym, otherFamilies: others };
}

/** Structured entries in `family` that spell `name` within the same brand scope. */
export function familyCopiesOf(
  family: FamilyTierNamesSource,
  name: string,
  brandId?: string,
): TierVocabularyEntry[] {
  const key = foldTierName(name);
  return (family.tierVocabulary ?? []).filter(
    (e) => (e.brandId ?? '') === (brandId ?? '') && [e.canonicalName, ...e.akas].some((n) => foldTierName(n) === key),
  );
}

// ---------------------------------------------------------------------------
// Calibre tier editor + AI apply — the combined list for one calibre
// ---------------------------------------------------------------------------

/** What the calibre editor knows about the two lists that govern its tier names. */
export interface CalibreTierVocabulary {
  manufacturerName?: string;
  manufacturer: TierVocabularyEntry[];
  /** e.g. "SW200" — absent when the calibre has no family. */
  familyName?: string;
  family: TierVocabularyEntry[];
  /** Legacy `calibre_families.tierNames`, read as unmarked generic family names. */
  legacyFamilyNames: string[];
}

export interface TierNameOption {
  name: string;
  /** Spellings that resolve to `name` (manufacturer synonyms + folded family drift). */
  akas: string[];
  source: 'manufacturer' | 'family';
  description?: string;
}

/**
 * The tier names a calibre's tier editor offers: the GENERIC grades only —
 * the manufacturer's by rank, then the family's additions. A family name that
 * spells a manufacturer grade is drift (D15): it becomes another spelling of
 * that grade, not a family option.
 *
 * Brand tier names are never offered (James, 2026-10-29): a brand-specific
 * tier lives on one calibre, and suggesting it anywhere else would carry one
 * brand's modification onto another brand's calibre. Any brand-scoped entry
 * still found in a list (none exist; the editors refuse new ones) is ignored.
 */
export function tierNameOptions(vocab: CalibreTierVocabulary): { manufacturer: TierNameOption[]; family: TierNameOption[] } {
  const generic = (e: TierVocabularyEntry) => !e.brandId;
  const manufacturer: TierNameOption[] = sortTierVocabulary(vocab.manufacturer.filter(generic)).map((e) => ({
    name: e.canonicalName,
    akas: [...e.akas],
    source: 'manufacturer',
    ...(e.description ? { description: e.description } : {}),
  }));
  const mfrKey = new Map<string, TierNameOption>();
  for (const o of manufacturer) for (const n of [o.name, ...o.akas]) mfrKey.set(foldTierName(n), o);

  const familyRaw: Array<{ name: string; akas: string[]; description?: string }> = [
    ...sortTierVocabulary(vocab.family.filter(generic)).map((e) => ({
      name: e.canonicalName,
      akas: e.akas,
      ...(e.description ? { description: e.description } : {}),
    })),
    ...vocab.legacyFamilyNames.map((n) => ({ name: n, akas: [] as string[] })),
  ];
  const family: TierNameOption[] = [];
  const seen = new Set<string>();
  for (const f of familyRaw) {
    const k = foldTierName(f.name);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const grade = mfrKey.get(k);
    if (grade) {
      for (const sp of [f.name, ...f.akas]) {
        const sk = foldTierName(sp);
        if ([grade.name, ...grade.akas].some((n) => foldTierName(n) === sk)) continue;
        grade.akas.push(sp);
      }
      continue;
    }
    family.push({ name: f.name, akas: [...f.akas], source: 'family', ...(f.description ? { description: f.description } : {}) });
  }
  return { manufacturer, family };
}

export type TypedTierNameResolution =
  | { kind: 'exact'; name: string }
  | { kind: 'spelling'; canonical: string; source: TierNameOption['source'] }
  | { kind: 'none' };

/**
 * What a typed tier name means against the offered options.
 * 'exact' = the canonical, ignoring case only (use the canonical's casing).
 * 'spelling' = another spelling of a listed name — an accent/case variant or a
 * synonym ("Special" → "Elaboré"); offer the canonical.
 */
export function resolveTypedTierName(typed: string, options: TierNameOption[]): TypedTierNameResolution {
  const t = typed.trim();
  if (!t) return { kind: 'none' };
  const exact = options.find((o) => o.name.toLowerCase() === t.toLowerCase());
  if (exact) return { kind: 'exact', name: exact.name };
  const k = foldTierName(t);
  const spelling = options.find((o) => [o.name, ...o.akas].some((n) => foldTierName(n) === k));
  if (spelling) return { kind: 'spelling', canonical: spelling.name, source: spelling.source };
  return { kind: 'none' };
}

/**
 * The canonical a stored tier name should be renamed to, or null when it
 * already is one (or matches nothing). "Special (Elaboré)" → "Elaboré" when
 * the manufacturer lists it as a spelling of Elaboré.
 */
export function canonicalRenameFor(tierName: string, options: TierNameOption[]): string | null {
  const res = resolveTypedTierName(tierName, options);
  if (res.kind !== 'spelling') return null;
  return res.canonical === tierName.trim() ? null : res.canonical;
}
